import React, { useEffect, useState } from "react";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useAssetLocations from "../../../../store/ReactQueries/assets/assetLocations/useAssetLocations";
import useAddAssLocations from "../../../../store/ReactQueries/assets/assetLocations/useAddAssetLocations";
import useUpdateAssetLocation from "../../../../store/ReactQueries/assets/assetLocations/useUpdateAssetLocation";
import useDeleteAssetLocation from "../../../../store/ReactQueries/assets/assetLocations/useDeleteAssetLocation";
import { GridColDef } from "@mui/x-data-grid";
import { PAGESIZE } from "../../../../utils/constants";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { formatDate } from "../../../../common/GlobalFunctions";
import { validateSchema } from "../../../../common/ValidateSchema";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { AssetLocationInterface } from "../../../../models/AssetModel/AssetLocation";
import { assetLocationsSchema } from "../../../../domain/ValidationSchemas/Asset/assetLocation";

export const AssetLocations = () => {
  const { language } = useLanguageStore();

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGESIZE);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [form, setForm] = useState<AssetLocationInterface>({
    id: "",
    title: "",
  });

  const [modal, setModal] = useState<{
    add: boolean;
    edit: boolean;
    view: boolean;
  }>({
    add: false,
    edit: false,
    view: false,
  });

  let { data: assetLocations, error } = useAssetLocations(page, pageSize);

  const addAssetLocation = useAddAssLocations(page, pageSize);
  const updateAssetLocation = useUpdateAssetLocation(page, pageSize);
  const deleteAssetLocation = useDeleteAssetLocation(page, pageSize);

  useEffect(() => {
    if (error) ToastMessage.Failed(`Error: ${error.message}`);
  }, [error]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      type: "string",
      editable: false,
      width: 200,
    },
    {
      field: "title",
      headerName: "Title",
      type: "string",
      editable: true,
      width: 250,
    },
    {
      field: "createdDate",
      headerName: "CreatedDate",
      type: "dateTime",
      editable: false,
      width: 150,
      valueFormatter: formatDate,
    },
    {
      field: "updatedDate",
      headerName: "UpdatedDate",
      type: "dateTime",
      editable: false,
      width: 150,
      valueFormatter: formatDate,
    },
  ];

  const addItems = () => {
    setForm({
      ...form,
      title: "",
      id: "",
    });
    setModal({ ...modal, add: true });
    setErrors({});
  };

  const handleError = (error: unknown) => {
    if (error instanceof Error) {
      console.log(error.message);
      ToastMessage.Failed(error.message);
    } else {
      console.log("An unknown error occurred");
      ToastMessage.Failed();
    }
  };

  const handleSave = async () => {
    const isValid = validateSchema(assetLocationsSchema, { title: form.title });

    setErrors(isValid.errors);
    if (!isValid.result) return;

    const upForm: AssetLocationInterface = {
      id: form.id,
      title: form.title,
    };

    if (upForm.id === "") {
      const { id, ...formData } = upForm;
      addAssetLocation.mutate(formData, {
        onSuccess: () => {
          ToastMessage.Success();
        },
        onError: (error) => handleError(error),
      });
    } else {
      updateAssetLocation.mutate(upForm, {
        onSuccess: () => {
          ToastMessage.Success();
        },
        onError: (error) => handleError(error),
      });
    }

    setModal({ ...modal, add: false, edit: false, view: false });
  };

  const handleDelete = async (id: string) => {
    if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
      deleteAssetLocation.mutate(id, {
        onSuccess: () => ToastMessage.Success(),
        onError: (error) => handleError(error),
      });
    }
  };

  return (
    <CustomDataGrid
      name={language["AssetLocations"]}
      rows={assetLocations || []}
      form={form}
      page={page}
      modal={modal}
      errors={errors}
      columns={columns}
      pageSize={pageSize}
      setForm={setForm}
      setPage={setPage}
      setModal={setModal}
      addItems={addItems}
      setErrors={setErrors}
      handleSave={handleSave}
      setPageSize={setPageSize}
      handleDelete={handleDelete}
      showColumnModal={["title"]}
      hideColumns={["authorId", "id"]}
    />
  );
};
export default AssetLocations;
